

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faPlus,
  faEdit,
  faTrash,
  faChartLine,
  faUsers,
  faMapMarkedAlt,
  faHotel,
  faFileAlt,
  faCalendarAlt,
  faDollarSign,
  faUser,
  faUserShield,
  faToggleOn,
  faToggleOff,
  faCheckCircle,
  faTimesCircle,
  faSearch,
  faChevronDown,
  faChevronUp,
  faEllipsisV,
  faBell,
  faCog,
  faSave,
  faTimes,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import {
  getDatabase,
  ref,
  onValue,
  update,
  set,
  remove,
  get
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db } from "../../firebaseConfig";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [travelPackages, setTravelPackages] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [visas, setVisas] = useState([]);
  const [newPackage, setNewPackage] = useState({
    id: "",
    name: "",
    destination: "",
    duration: "",
    description: "",
    images: [],
    includes: [],
    excludes: [],
    itinerary: {},
    type: "domestic",
  });
  const [priceInput, setPriceInput] = useState("");
  const [priceValues, setPriceValues] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [includeInput, setIncludeInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    packages: true,
    blogs: true,
    agents: true,
    enquiries: true,
  });
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      message: "New booking received from John Doe",
      time: "2 mins ago",
      read: false,
    },
    {
      id: 2,
      message: "Agent registration approved",
      time: "1 hour ago",
      read: true,
    },
    {
      id: 3,
      message: "System maintenance scheduled",
      time: "Yesterday",
      read: true,
    },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [newBlog, setNewBlog] = useState({
    title: "",
    content: "",
    author: "",
    category: "general",
    featuredImage: null,
  });
  const [isEditingBlog, setIsEditingBlog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState({
    packages: 0,
    bookings: 0,
    blogs: 0,
    revenue: 24500,
    agents: 0,
    activeAgents: 0,
    visas: 0,
  });
  const [visaData, setVisaData] = useState({
    visitingCountry: "",
    fees: "",
    documentsRequired: [],
    flag: "",
  });
  const [currentInput, setCurrentInput] = useState({
    documentsRequired: "",
  });
  const [isEditingVisa, setIsEditingVisa] = useState(false);
  const [currentVisaId, setCurrentVisaId] = useState(null);
  const [errors, setErrors] = useState({});

  const database = getDatabase();
  const storage = getStorage();
  const fileInputRef = useRef(null);
  const packageImagesRef = useRef(null);
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
  };

  const handleAddPrice = () => {
    if (priceInput.trim()) {
      setPriceValues([...priceValues, priceInput.trim()]);
      setPriceInput("");
    }
  };

  const handleRemovePrice = (index) => {
    setPriceValues(priceValues.filter((_, i) => i !== index));
  };

  const handlePriceKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddPrice();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPackage((prev) => ({
      ...prev,
      [name]: type === "radio" ? value : value,
    }));
  };

  const handlePackageImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(files);
  };

  const handleAddInclude = () => {
    if (includeInput.trim()) {
      setNewPackage((prev) => ({
        ...prev,
        includes: [...prev.includes, includeInput.trim()],
      }));
      setIncludeInput("");
    }
  };

  const handleRemoveInclude = (index) => {
    setNewPackage((prev) => ({
      ...prev,
      includes: prev.includes.filter((_, i) => i !== index),
    }));
  };

  const handleAddExclude = () => {
    if (excludeInput.trim()) {
      setNewPackage((prev) => ({
        ...prev,
        excludes: [...prev.excludes, excludeInput.trim()],
      }));
      setExcludeInput("");
    }
  };

  const handleRemoveExclude = (index) => {
    setNewPackage((prev) => ({
      ...prev,
      excludes: prev.excludes.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveImage = (index, isExisting = true) => {
    if (isExisting) {
      // For existing images, we'll keep them in state but mark them for removal
      // This way we can delete them from storage when the form is submitted
      setExistingImages(prev => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    } else {
      // For new images that haven't been uploaded yet
      setNewImages(prev => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    }
  };

  const handleAddDay = () => {
    const dayCount = Object.keys(newPackage.itinerary).length + 1;
    const newDay = `Day ${dayCount}`;
    setNewPackage((prev) => ({
      ...prev,
      itinerary: { ...prev.itinerary, [newDay]: "" },
    }));
  };

  const handleRemoveDay = (day) => {
    const { [day]: removed, ...remainingDays } = newPackage.itinerary;
    setNewPackage((prev) => ({ ...prev, itinerary: remainingDays }));
  };

  const handleItineraryChange = (day, content) => {
    setNewPackage((prev) => ({
      ...prev,
      itinerary: { ...prev.itinerary, [day]: content },
    }));
  };

  const uploadPdf = async () => {
    if (!pdfFile) return null;

    try {
      const pdfRef = storageRef(
        storage,
        `package-pdfs/${Date.now()}_${pdfFile.name}`
      );
      await uploadBytes(pdfRef, pdfFile);
      return await getDownloadURL(pdfRef);
    } catch (error) {
      console.error("Error uploading PDF:", error);
      throw error;
    }
  };

  const uploadPackageImages = async () => {
    if (!newImages.length) return [];

    try {
      const uploadPromises = newImages.map(async (image) => {
        const imageRef = storageRef(
          storage,
          `package-images/${Date.now()}_${image.name}`
        );
        await uploadBytes(imageRef, image);
        return await getDownloadURL(imageRef);
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading images:", error);
      throw error;
    }
  };

  const processPriceValues = (priceValues, amount = 2000) => {
    if (!priceValues) return null;

    const processPriceString = (priceStr) => {
      if (typeof priceStr !== 'string') return priceStr;

      return priceStr.replace(/\d[\d,]*/g, (match) => {
        const number = parseInt(match.replace(/,/g, ''), 10);
        const newNumber = Math.max(0, number - amount); 
        return newNumber.toLocaleString('en-IN'); 
      });
    };

    if (Array.isArray(priceValues)) {
      return priceValues.map(processPriceString);
    } else if (typeof priceValues === 'string') {
      return processPriceString(priceValues);
    } else if (typeof priceValues === 'object') {
      return Object.fromEntries(
        Object.entries(priceValues).map(([key, value]) => [
          key,
          processPriceString(value),
        ])
      );
    }

    return priceValues;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!newPackage.name) {
      newErrors.name = 'Package name is required';
      isValid = false;
    }
    
    if (!newPackage.destination) {
      newErrors.destination = 'Destination is required';
      isValid = false;
    }
    
    if (!newPackage.duration) {
      newErrors.duration = 'Duration is required';
      isValid = false;
    }
    
    if (!newPackage.description) {
      newErrors.description = 'Description is required';
      isValid = false;
    }
    
    if (!newPackage.type) {
      newErrors.type = 'Package type is required';
      isValid = false;
    }
    
    if (priceValues.length === 0) {
      newErrors.price = 'At least one price is required';
      isValid = false;
    } else {
      const hasInvalidPrice = priceValues.some(price => {
        const priceStr = String(price).replace(/[^0-9]/g, '');
        return priceStr.length === 0 || isNaN(parseInt(priceStr));
      });
      
      if (hasInvalidPrice) {
        newErrors.price = 'Please enter valid price values';
        isValid = false;
      }
    }
    
    if (existingImages.length === 0 && newImages.length === 0) {
      newErrors.images = 'At least one image is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddPackage = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      const newImageUrls = await uploadPackageImages();
      const allImages = [...existingImages, ...newImageUrls];
      const pdfUrl = await uploadPdf();

      const currentPriceValues = priceValues.length > 0 ? priceValues : [];
      
      const discountedPriceValues = processPriceValues(currentPriceValues, 2000);

      const packageData = {
        ...newPackage,
        priceValues: currentPriceValues, 
        images: allImages,
        pdfUrl: pdfUrl || null,
        createdAt: new Date().toISOString(),
      };

      const agentPackageData = {
        ...newPackage,
        priceValues: discountedPriceValues || [], 
        images: allImages,
        pdfUrl: pdfUrl || null,
        createdAt: new Date().toISOString(),
      };

      const packageDbRef = ref(
        database,
        `packages/${newPackage.destination}/${newPackage.name}`
      );

      const agentPackageDbRef = ref(
        database,
        `AgentPackages/${newPackage.type === 'international' ? 'international' : 'domestic'}/${newPackage.destination}/${newPackage.name}`
      );

      if (isEditing && newPackage.id) {
        await Promise.all([
          update(packageDbRef, packageData), 
          update(agentPackageDbRef, agentPackageData), 
        ]);
      } else {
        await Promise.all([
          set(packageDbRef, packageData), 
          set(agentPackageDbRef, agentPackageData), 
        ]);
      }

      resetForm();
      alert("Package saved successfully with discounted price in AgentPackages!");
    } catch (error) {
      console.error("Error saving package:", error);
      alert(`Error saving package: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewPackage({
      id: "",
      name: "",
      destination: "",
      duration: "",
      description: "",
      images: [],
      includes: [],
      excludes: [],
      itinerary: {},
      type: "domestic",
    });
    setPriceValues([]);
    setPriceInput("");
    setExistingImages([]);
    setNewImages([]);
    setPdfFile(null);
    setErrors({});
    if (packageImagesRef.current) {
      packageImagesRef.current.value = "";
    }
    setIsEditing(false);
  };

  const handleEditPackage = (pkg) => {
    resetForm(); // Clear the form first
    setNewPackage({
      id: pkg.id || pkg.name, // Use name as fallback ID
      name: pkg.name,
      destination: pkg.destination,
      duration: pkg.duration,
      description: pkg.description,
      images: pkg.images || [],
      includes: pkg.includes || [],
      excludes: pkg.excludes || [],
      itinerary: pkg.itinerary || {},
      type: pkg.type || "domestic",
    });
    setPriceValues(pkg.priceValues || []);
    setExistingImages(pkg.images || []);
    setIsEditing(true);
  };








  const handleDeletePackage = async (pkg) => {
  if (
    window.confirm(`Are you sure you want to delete "${pkg.name}" package?`)
  ) {
    try {
      // Delete package from all locations
      const deletePromises = [
        // Delete from packages/destination
        remove(ref(database, `packages/${pkg.destination}/${pkg.id || pkg.name}`)),
        
        // Delete from AgentPackages (assuming similar structure)
        remove(ref(database, `AgentPackages/${pkg.destination}/${pkg.id || pkg.name}`))
      ];

      await Promise.all(deletePromises);

      // Delete associated images if they exist
      if (pkg.images && pkg.images.length > 0) {
        const imageDeletePromises = pkg.images.map((imgUrl) => {
          const imageRef = storageRef(storage, imgUrl);
          return deleteObject(imageRef).catch((error) => {
            console.error("Error deleting image:", error);
          });
        });
        await Promise.all(imageDeletePromises);
      }

      alert("Package deleted successfully from all locations");
    } catch (error) {
      console.error("Error deleting package:", error);
      alert("Error deleting package");
    }
  }
};






  const handleBlogInputChange = (e) => {
    const { name, value } = e.target;
    setNewBlog((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddBlog = async () => {
    if (!newBlog.title || !newBlog.content) {
      alert("Please fill in title and content");
      return;
    }

    try {
      setIsSubmitting(true);

      let featuredImageUrl = newBlog.featuredImage;
      if (newBlog.featuredImage instanceof File) {
        const imageRef = storageRef(
          storage,
          `blogs/${Date.now()}_${newBlog.featuredImage.name}`
        );
        const snapshot = await uploadBytes(imageRef, newBlog.featuredImage);
        featuredImageUrl = await getDownloadURL(snapshot.ref);
      }

      const blogData = {
        ...newBlog,
        featuredImage: featuredImageUrl,
        id: currentBlogId || Date.now().toString(),
        createdAt: Date.now(),
      };

      const blogRef = ref(database, `blogs/${blogData.id}`);
      await set(blogRef, blogData);

      alert(
        isEditingBlog
          ? "Blog updated successfully!"
          : "Blog published successfully!"
      );
      resetBlogForm();
    } catch (error) {
      console.error("Error saving blog:", error);
      alert("Error saving blog");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetBlogForm = () => {
    setNewBlog({
      title: "",
      content: "",
      author: "",
      category: "general",
      featuredImage: null,
    });
    setIsEditingBlog(false);
    setCurrentBlogId(null);
  };

  const handleEditBlog = (blog) => {
    resetBlogForm();
    setNewBlog({
      title: blog.title,
      content: blog.content,
      author: blog.author,
      category: blog.category || "general",
      featuredImage: blog.featuredImage,
    });
    setCurrentBlogId(blog.id);
    setIsEditingBlog(true);
  };

  // Fixed blog delete functionality
  const handleDeleteBlog = async (blog) => {
    if (
      window.confirm(`Are you sure you want to delete "${blog.title}" blog?`)
    ) {
      try {
        const blogRef = ref(database, `blogs/${blog.id}`);
        await remove(blogRef);

        if (blog.featuredImage) {
          const imageRef = storageRef(storage, blog.featuredImage);
          await deleteObject(imageRef).catch((error) => {
            console.error("Error deleting blog image:", error);
          });
        }

        alert("Blog deleted successfully");
      } catch (error) {
        console.error("Error deleting blog:", error);
        alert("Error deleting blog");
      }
    }
  };
  const toggleAgentStatus = async (agent) => {
    const newStatus = !agent.isActive;
    if (
      window.confirm(
        `Are you sure you want to ${newStatus ? "activate" : "deactivate"} ${
          agent.firstName
        } ${agent.lastName}?`
      )
    ) {
      try {
        const agentRef = ref(database, `Agent/${agent.id}/isActive`);
        await set(agentRef, newStatus);

        setAgents((prevAgents) =>
          prevAgents.map((a) =>
            a.id === agent.id ? { ...a, isActive: newStatus } : a
          )
        );

        setStats((prev) => ({
          ...prev,
          activeAgents: newStatus
            ? prev.activeAgents + 1
            : prev.activeAgents - 1,
        }));
      } catch (error) {
        console.error("Error updating agent status:", error);
        alert("Error updating agent status");
      }
    }
  };

  const handleDeleteAgent = async (agent) => {
    if (
      window.confirm(
        `Are you sure you want to delete agent ${agent.firstName} ${agent.lastName}?`
      )
    ) {
      try {
        await remove(ref(database, `Agent/${agent.id}`));
        alert("Agent deleted successfully");
      } catch (error) {
        console.error("Error deleting agent:", error);
        alert("Error deleting agent");
      }
    }
  };

  const handleEditEnquiry = (enquiry) => {
    alert(`Edit enquiry for ${enquiry.name}`);
  };

  // const handleDeleteEnquiry = async (mobile) => {
  //   if (window.confirm("Are you sure you want to delete this enquiry?")) {
  //     try {
  //       await remove(ref(database, `enquiry/${mobile}`));
  //       alert("Enquiry deleted successfully");
  //     } catch (error) {
  //       console.error("Error deleting enquiry:", error);
  //       alert("Error deleting enquiry");
  //     }
  //   }
  // };


  const handleDeleteEnquiry = async (enquiry) => {
    try {
      if (!enquiry || !enquiry.mobile || !enquiry.srNo) {
        alert("Invalid enquiry data");
        return;
      }

      const mobileNumber = String(enquiry.mobile).trim();
      const srNo = String(enquiry.srNo).trim();
      
      if (!mobileNumber || !srNo) {
        alert("Invalid enquiry data");
        return;
      }

      // Create the database reference
      const enquiryRef = ref(database, `enquiry/${mobileNumber}/${srNo}`);
      
      // Verify the enquiry exists first
      const snapshot = await get(enquiryRef);
      
      if (!snapshot.exists()) {
        alert("Enquiry not found");
        return;
      }

      // Confirm deletion
      const shouldDelete = window.confirm(
        `Are you sure you want to delete this enquiry?`
      );
      
      if (!shouldDelete) return;

      // Perform deletion
      await remove(enquiryRef);
      
      // Update local state
      setEnquiries(prev => prev.filter(e => 
        !(String(e.mobile) === mobileNumber && String(e.srNo) === srNo)
      ));
      
      alert("Enquiry deleted successfully");
      
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      alert(`Failed to delete enquiry: ${error.message}`);
    }
  };



  const markNotificationAsRead = (id) => {
    setNotifications(
      notifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    const packagesRef = ref(database, "packages");
    const packagesUnsubscribe = onValue(packagesRef, (snapshot) => {
      const data = snapshot.val();
      let packageCount = 0;
      let allPackages = [];

      if (data) {
        Object.values(data).forEach((cityPackages) => {
          packageCount += Object.keys(cityPackages).length;
          allPackages = [...allPackages, ...Object.values(cityPackages)];
        });
      }

      setTravelPackages(allPackages);
      setStats((prev) => ({ ...prev, packages: packageCount }));
    });

    const blogsRef = ref(database, "blogs");
    const blogsUnsubscribe = onValue(blogsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const blogsArray = Object.values(data);

      setBlogs(blogsArray);
      setStats((prev) => ({ ...prev, blogs: blogsArray.length }));
    });

    const agentsRef = ref(database, "Agent");
    const agentsUnsubscribe = onValue(agentsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const agentsArray = Object.entries(data).map(([id, agent]) => ({
        id,
        ...agent,
        isActive: agent.isActive !== undefined ? agent.isActive : true,
      }));

      const activeCount = agentsArray.filter((agent) => agent.isActive).length;

      setAgents(agentsArray);
      setStats((prev) => ({
        ...prev,
        agents: agentsArray.length,
        activeAgents: activeCount,
      }));
    });

    const enquiriesRef = ref(database, "enquiry");
    const enquiriesUnsubscribe = onValue(enquiriesRef, (snapshot) => {
      const data = snapshot.val();
      let enquiriesCount = 0;
      let enquiriesArray = [];

      if (data) {
        // Iterate through each mobile number
        Object.entries(data).forEach(([mobile, enquiries]) => {
          // Check if there are enquiries for this mobile number
          if (enquiries) {
            // If it's an object (not an array), convert it to an array of entries
            const enquiryEntries = Array.isArray(enquiries) 
              ? Object.entries(enquiries) 
              : Object.entries(enquiries);
            
            // Process each enquiry for this mobile number
            enquiryEntries.forEach(([srNo, enquiry]) => {
              if (enquiry) { // Ensure enquiry exists
                enquiriesCount++;
                enquiriesArray.push({
                  mobile,
                  srNo,
                  ...enquiry,
                  // Add a unique ID for React keys
                  id: `${mobile}-${srNo}`
                });
              }
            });
          }
        });
      }

      // Sort by creation date (newest first)
      enquiriesArray.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });

      setEnquiries(enquiriesArray);
      setStats((prev) => ({ ...prev, bookings: enquiriesCount }));
    });

    const visasRef = ref(database, "visa");
    const visasUnsubscribe = onValue(visasRef, (snapshot) => {
      const data = snapshot.val() || {};
      const visasArray = Object.entries(data).map(([id, visa]) => ({
        id,
        ...visa,
      }));

      setVisas(visasArray);
      setStats((prev) => ({ ...prev, visas: visasArray.length }));
    });

    return () => {
      packagesUnsubscribe();
      blogsUnsubscribe();
      agentsUnsubscribe();
      enquiriesUnsubscribe();
      visasUnsubscribe();
    };
  }, [database]);

  const filteredPackages = travelPackages.filter((pkg) => {
    const name = pkg.name || "";
    const destination = pkg.destination || "";
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      destination.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const filteredBlogs = blogs.filter((blog) => {
    const title = blog.title || "";
    const author = blog.author || "";
    const category = blog.category || "";
    return (
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const filteredAgents = agents.filter((agent) => {
    const firstName = agent.firstName || "";
    const lastName = agent.lastName || "";
    const email = agent.email || "";
    const mobile = agent.mobile || "";
    return (
      firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mobile.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const filteredEnquiries = enquiries.filter((enquiry) => {
    const name = enquiry.name || "";
    const destination = enquiry.destination || "";
    const mobile = enquiry.mobile || "";
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mobile.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const filteredVisas = visas.filter((visa) => {
    const country = visa.visitingCountry || "";
    const fees = visa.fees || "";
    return (
      country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fees.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const StatCard = ({ icon, value, label, color }) => (
    <div
      className="stat-card"
      style={{
        background: `linear-gradient(135deg, ${color} 0%, ${adjustColor(
          color,
          20
        )} 100%)`,
        boxShadow: `0 4px 6px rgba(${hexToRgb(color)}, 0.1)`,
      }}
    >
      <div className="stat-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="stat-content">
        <h3>{value}</h3>
        <p>{label}</p>
      </div>
    </div>
  );

  const adjustColor = (color, amount) => {
    return (
      "#" +
      color
        .replace(/^#/, "")
        .replace(/../g, (color) =>
          (
            "0" +
            Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(
              16
            )
          ).substr(-2)
        )
    );
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
          result[3],
          16
        )}`
      : "0, 0, 0";
  };

  const handleAddItem = (field) => {
    if (currentInput[field].trim()) {
      setVisaData((prev) => ({
        ...prev,
        [field]: [...prev[field], currentInput[field].trim()],
      }));
      setCurrentInput((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleRemoveItem = (field, index) => {
    setVisaData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSaveVisa = async () => {
    try {
      if (!visaData.visitingCountry) {
        alert("Please enter a visiting country");
        return;
      }

      const countryRes = await fetch(
        `https://restcountries.com/v3.1/name/${visaData.visitingCountry}?fullText=true`
      );
      const countryData = await countryRes.json();

      if (!countryData || !countryData[0]?.flags?.png) {
        alert("Could not find flag for the entered country.");
        return;
      }

      const flagUrl = countryData[0].flags.png;

      const visitingCountryPath = visaData.visitingCountry
        .toLowerCase()
        .replace(/\s+/g, "-");
      const visaEntryPath = `visa/${visitingCountryPath}`;

      const visaEntryRef = ref(database, visaEntryPath);
      await set(visaEntryRef, {
        ...visaData,
        flag: flagUrl,
        createdAt: Date.now(),
      });

      alert("Visa information saved successfully!");
      resetVisaForm();
    } catch (error) {
      console.error("Error saving visa information:", error);
      alert("Error saving visa information: " + error.message);
    }
  };

  const resetVisaForm = () => {
    setVisaData({
      visitingCountry: "",
      fees: "",
      documentsRequired: [],
      flag: "",
    });
    setIsEditingVisa(false);
    setCurrentVisaId(null);
  };
  const handleEditVisa = (visa) => {
    resetVisaForm();
    setIsEditingVisa(true);
    setCurrentVisaId(visa.id);
    setVisaData({
      visitingCountry: visa.visitingCountry,
      fees: visa.fees,
      documentsRequired: visa.documentsRequired || [],
      flag: visa.flag || "",
    });
  };

  // Fixed visa delete
  const handleDeleteVisa = async (visa) => {
    if (
      window.confirm(
        `Are you sure you want to delete visa information for ${visa.visitingCountry}?`
      )
    ) {
      try {
        const visaRef = ref(database, `visa/${visa.id}`);
        await remove(visaRef);
        alert("Visa information deleted successfully");
      } catch (error) {
        console.error("Error deleting visa:", error);
        alert("Error deleting visa information");
      }
    }
  };

  const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
  
  :root {
    --primary: #64192e;
    --primary-light: #8c2441;
    --secondary: #38bdf8;
    --success: #10b981;
    --danger: #ef4444;
    --warning: #f59e0b;
    --info: #3b82f6;
    --dark: #1e293b;
    --light: #f8fafc;
    --gray: #94a3b8;
    --gray-light: #e2e8f0;
    --white: #ffffff;
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-md: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-lg: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    --radius-sm: 0.25rem;
    --radius: 0.5rem;
    --radius-md: 0.75rem;
    --radius-lg: 1rem;
    --transition: all 0.2s ease-in-out;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Poppins', sans-serif;
  }
  
  body {
    background-color: #f5f7fa;
  }

  .admin-dashboard {
    display: flex;
    min-height: 100vh;
    background-color: var(--light);
  }

  /* Sidebar Styles */
  .admin-sidebar {
    width: 280px;
    background: linear-gradient(180deg, var(--primary) 0%, #4a1322 100%);
    color: var(--white);
    display: flex;
    flex-direction: column;
    transition: all 0.3s;
    box-shadow: var(--shadow-md);
    z-index: 100;
  }

  .sidebar-header {
    padding: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .admin-logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .logo-icon {
    font-size: 1.75rem;
    color: var(--white);
  }

  .logo-text {
    font-weight: 600;
    font-size: 1.25rem;
    color: var(--white);
  }

  .sidebar-menu {
    list-style: none;
    padding: 0;
    margin: 0;
    flex-grow: 1;
    padding-top: 1rem;
  }

  .sidebar-menu li {
    padding: 0.875rem 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    transition: var(--transition);
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
    border-left: 3px solid transparent;
    margin: 0 0.5rem;
    border-radius: var(--radius-sm);
  }

  .sidebar-menu li:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--white);
  }

  .sidebar-menu li.active {
    background-color: rgba(255, 255, 255, 0.15);
    color: var(--white);
    border-left-color: var(--white);
  }

  .sidebar-menu li.active svg {
    color: var(--white);
  }

  .sidebar-footer {
    padding: 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .logout-button {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    width: 100%;
    padding: 0.625rem 1rem;
    border-radius: var(--radius-sm);
    transition: var(--transition);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .logout-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--white);
  }

  /* Main Content Styles */
  .admin-content {
    flex-grow: 1;
    overflow-y: auto;
  }

  .admin-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 2rem;
    background-color: var(--white);
    box-shadow: var(--shadow-sm);
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .page-title {
    color: var(--dark);
    margin: 0;
    font-size: 1.375rem;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .search-bar {
    position: relative;
    width: 18.75rem;
  }

  .search-bar input {
    width: 100%;
    padding: 0.625rem 1rem 0.625rem 2.5rem;
    border: 1px solid var(--gray-light);
    border-radius: var(--radius);
    font-size: 0.875rem;
    background-color: var(--light);
    transition: var(--transition);
  }

  .search-bar input:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(100, 25, 46, 0.2);
    background-color: var(--white);
  }

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--gray);
    font-size: 0.875rem;
  }

  .notification-btn, .user-menu-btn {
    background: none;
    border: none;
    color: var(--gray);
    cursor: pointer;
    position: relative;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--transition);
  }

  .notification-btn:hover, .user-menu-btn:hover {
    background-color: var(--gray-light);
    color: var(--dark);
  }

  .notification-badge {
    position: absolute;
    top: 0.3125rem;
    right: 0.3125rem;
    background-color: var(--danger);
    color: var(--white);
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
  }

  .user-avatar {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background-color: var(--primary);
    color: var(--white);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
  }

  /* Dropdown Styles */
  .notifications-dropdown {
    position: absolute;
    top: 3.75rem;
    right: 1.25rem;
    width: 21.875rem;
    background-color: var(--white);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    overflow: hidden;
    z-index: 60;
    transform-origin: top right;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-0.625rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .notifications-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--gray-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .notifications-header h3 {
    margin: 0;
    font-size: 1rem;
    color: var(--dark);
  }

  .notifications-list {
    max-height: 25rem;
    overflow-y: auto;
  }

  .notification-item {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--gray-light);
    cursor: pointer;
    transition: var(--transition);
  }

  .notification-item.unread {
    background-color: var(--light);
  }

  .notification-item:hover {
    background-color: var(--gray-light);
  }

  .notification-message {
    font-size: 0.875rem;
    color: var(--dark);
    margin-bottom: 0.3125rem;
  }

  .notification-time {
    font-size: 0.75rem;
    color: var(--gray);
  }

  .user-dropdown {
    position: absolute;
    top: 3.75rem;
    right: 1.25rem;
    width: 13.75rem;
    background-color: var(--white);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    overflow: hidden;
    z-index: 60;
    animation: fadeIn 0.2s ease-out;
  }

  .user-info {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--gray-light);
  }

  .user-name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--dark);
    margin-bottom: 0.1875rem;
  }

  .user-email {
    font-size: 0.8125rem;
    color: var(--gray);
  }

  .user-menu-list {
    padding: 0.625rem 0;
  }

  .user-menu-item {
    padding: 0.625rem 1.25rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-size: 0.875rem;
    color: var(--red);
    transition: var(--transition);
  }

  .user-menu-item:hover {
    background-color: var(--gray-light);
    color: var(--dark);
  }

  /* Dashboard Overview */
  .dashboard-overview {
    padding: 2rem;
  }

  .stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    gap: 1.25rem;
    margin-bottom: 2rem;
  }

  .stat-card {
    border-radius: var(--radius-md);
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1.25rem;
    color: var(--white);
    transition: var(--transition);
    box-shadow: var(--shadow);
  }

  .stat-card:hover {
    transform: translateY(-0.3125rem);
    box-shadow: var(--shadow-md);
  }

  .stat-icon {
    width: 3.75rem;
    height: 3.75rem;
    border-radius: var(--radius);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    background-color: rgba(255, 255, 255, 0.2);
  }

  .stat-content h3 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 600;
  }

  .stat-content p {
    margin: 0.3125rem 0 0;
    font-size: 0.875rem;
    opacity: 0.9;
  }

  /* Section Styles */
  .dashboard-sections {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .dashboard-section {
    background-color: var(--white);
    border-radius: var(--radius-md);
    padding: 1.5rem;
    box-shadow: var(--shadow-sm);
    transition: var(--transition);
  }

  .dashboard-section:hover {
    box-shadow: var(--shadow);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    cursor: pointer;
  }

  .section-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--dark);
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .section-toggle {
    color: var(--gray);
    transition: var(--transition);
  }

  .section-content {
    overflow: hidden;
    transition: var(--transition);
  }

  /* Table Styles */
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .data-table th {
    background-color: var(--light);
    color: var(--gray);
    font-weight: 500;
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--gray-light);
  }

  .data-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--gray-light);
    color: var(--dark);
  }

  .data-table tr:last-child td {
    border-bottom: none;
  }

  .data-table tr:hover td {
    background-color: var(--light);
  }

  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .status-active {
    background-color: #dcfce7;
    color: #166534;
  }

  .status-inactive {
    background-color: #fee2e2;
    color: #dc2626;
  }

  /* Action Buttons */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    width: 1.875rem;
    height: 1.875rem;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--gray);
    transition: var(--transition);
  }

  .action-btn:hover {
    background-color: var(--gray-light);
  }

  .edit-btn:hover {
    color: var(--primary);
  }

  .delete-btn:hover {
    color: var(--danger);
  }

  /* Form Styles */
  .form-container {
    background-color: var(--white);
    border-radius: var(--radius-md);
    padding: 1.5rem;
    box-shadow: var(--shadow-sm);
    margin-bottom: 2rem;
  }

  .form-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--dark);
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group.full-width {
    grid-column: 1 / -1;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--dark);
  }
  
  .error-message {
    color: var(--danger);
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
  
  .is-invalid {
    border-color: var(--danger) !important;
  }
  
  .is-invalid:focus {
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
  
  .radio-group.error {
    border: 1px solid var(--danger);
    padding: 0.5rem;
    border-radius: var(--radius-sm);
  }

  .form-control {
    width: 100%;
    padding: 0.625rem 1rem;
    border: 1px solid var(--gray-light);
    border-radius: var(--radius);
    font-size: 0.875rem;
    transition: var(--transition);
    background-color: var(--light);
  }

  .form-control:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(100, 25, 46, 0.2);
  }

  textarea.form-control {
    min-height: 7.5rem;
    resize: vertical;
  }

  /* Radio Group Styles */
  .radio-group {
    display: flex;
    gap: 1.25rem;
    margin-top: 0.5rem;
  }

  .radio-option {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .radio-option input[type="radio"] {
    margin-right: 0.5rem;
    appearance: none;
    width: 1rem;
    height: 1rem;
    border: 2px solid var(--gray-light);
    border-radius: 50%;
    outline: none;
    transition: var(--transition);
  }

  .radio-option input[type="radio"]:checked {
    border-color: var(--primary);
    background-color: var(--primary);
    box-shadow: inset 0 0 0 2px var(--white);
  }

  .radio-label {
    font-size: 0.875rem;
    color: var(--dark);
  }

  /* Chip Styles */
  .chip-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.625rem;
  }

  .chip {
    background-color: var(--gray-light);
    color: var(--dark);
    padding: 0.375rem 0.75rem;
    border-radius: 1.25rem;
    font-size: 0.8125rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    transition: var(--transition);
  }

  .chip:hover {
    background-color: #d1d5db;
  }

  .chip-remove {
    cursor: pointer;
    color: var(--gray);
    font-size: 0.75rem;
    transition: color 0.2s;
  }

  .chip-remove:hover {
    color: var(--danger);
  }

  .chip-input-container {
    display: flex;
    gap: 0.625rem;
    margin-top: 0.625rem;
  }

  .chip-input {
    flex-grow: 1;
  }

  .add-chip-btn {
    background-color: var(--primary);
    color: var(--white);
    border: none;
    border-radius: var(--radius-sm);
    padding: 0 1rem;
    cursor: pointer;
    transition: var(--transition);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .add-chip-btn:hover {
    background-color: var(--primary-light);
  }

  /* Itinerary Styles */
  .itinerary-container {
    margin-top: 1rem;
  }

  .itinerary-day {
    margin-bottom: 1rem;
    border: 1px solid var(--gray-light);
    border-radius: var(--radius);
    overflow: hidden;
    transition: var(--transition);
  }

  .itinerary-day:hover {
    border-color: var(--primary);
  }

  .itinerary-header {
    background-color: var(--light);
    padding: 0.625rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--gray-light);
  }

  .remove-day-btn {
    background: none;
    border: none;
    color: var(--gray);
    cursor: pointer;
    transition: color 0.2s;
  }

  .remove-day-btn:hover {
    color: var(--danger);
  }

  .itinerary-content {
    padding: 1rem;
  }

  .add-day-btn {
    background-color: var(--light);
    color: var(--dark);
    border: none;
    border-radius: var(--radius-sm);
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.625rem;
    transition: var(--transition);
  }

  .add-day-btn:hover {
    background-color: var(--gray-light);
  }

  /* File Upload Styles */
  .file-upload {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .file-upload-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
    margin-top: 0.5rem;
  }

  .image-preview-container {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .image-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-image-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    font-size: 12px;
    transition: all 0.2s ease;
  }

  .remove-image-btn:hover {
    background: #ff0000;
    transform: scale(1.1);
  }

  .image-preview {
    width: 6.25rem;
    height: 6.25rem;
    border-radius: var(--radius);
    object-fit: cover;
    border: 1px solid var(--gray-light);
    transition: var(--transition);
  }

  .image-preview:hover {
    transform: scale(1.05);
  }

  .file-input-label {
    display: inline-block;
    background-color: var(--light);
    color: var(--dark);
    padding: 0.5rem 1rem;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: 0.875rem;
    transition: var(--transition);
    text-align: center;
    width: fit-content;
  }

  .file-input-label:hover {
    background-color: var(--gray-light);
  }

  .file-input {
    display: none;
  }

  /* Form Actions */
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.9375rem;
    margin-top: 1.5rem;
  }

  .btn {
    padding: 0.625rem 1.25rem;
    border-radius: var(--radius);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: var(--transition);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-primary {
    background-color: var(--primary);
    color: var(--white);
    border: none;
  }

  .btn-primary:hover {
    background-color: var(--primary-light);
  }

  .btn-secondary {
    background-color: var(--light);
    color: var(--dark);
    border: none;
  }

  .btn-secondary:hover {
    background-color: var(--gray-light);
  }

  .btn-danger {
    background-color: #fee2e2;
    color: var(--danger);
    border: none;
  }

  .btn-danger:hover {
    background-color: #fecaca;
  }

  /* Price Inputs */
  .price-inputs-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(12.5rem, 1fr));
    gap: 0.9375rem;
    margin-top: 0.625rem;
  }

  .price-input-group {
    display: flex;
    flex-direction: column;
  }

  .price-label {
    font-size: 0.8125rem;
    margin-bottom: 0.3125rem;
    color: var(--gray);
    text-transform: capitalize;
  }

  .price-input {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--gray-light);
    border-radius: var(--radius-sm);
    width: 100%;
    font-size: 0.875rem;
  }

  .price-input:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(100, 25, 46, 0.2);
  }

  /* Price Display */
  .price-display {
    display: flex;
    flex-direction: column;
    gap: 0.3125rem;
  }

  .price-item {
    display: flex;
    justify-content: space-between;
    font-size: 0.8125rem;
  }

  .price-type {
    color: var(--gray);
    text-transform: capitalize;
  }

  .price-value {
    font-weight: 500;
    color: var(--dark);
  }

  /* Responsive Styles */
  @media (max-width: 1200px) {
    .admin-sidebar {
      width: 15.625rem;
    }
  }

  @media (max-width: 992px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .admin-sidebar {
      position: fixed;
      left: -17.5rem;
      top: 0;
      bottom: 0;
      z-index: 100;
    }

    .admin-sidebar.open {
      left: 0;
    }

    .admin-content {
      margin-left: 0;
    }

    .stats-container {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 576px) {
    .admin-header {
      padding: 0.9375rem;
    }

    .search-bar {
      width: auto;
    }

    .header-actions {
      gap: 0.625rem;
    }

    .stats-container {
      grid-template-columns: 1fr;
    }

    .notifications-dropdown {
      width: 17.5rem;
      right: 0.625rem;
    }
  }

  /* Animation for form elements */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(1.25rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .form-group {
    animation: fadeInUp 0.3s ease-out forwards;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 0.5rem;
    height: 0.5rem;
  }

  ::-webkit-scrollbar-track {
    background: var(--gray-light);
    border-radius: 0.25rem;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 0.25rem;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--primary-light);
  }

  /* Tooltip styles */
  [data-tooltip] {
    position: relative;
    cursor: pointer;
  }

  [data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--dark);
    color: var(--white);
    padding: 0.25rem 0.5rem;
    border-radius: var(--radius-sm);
    font-size: 0.75rem;
    white-space: nowrap;
    z-index: 100;
    margin-bottom: 0.5rem;
  }

  /* Loading spinner */
  .spinner {
    width: 1.5rem;
    height: 1.5rem;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: var(--white);
    animation: spin 1s ease-in-out infinite;
    display: inline-block;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Toast notification */
  .toast {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    background-color: var(--primary);
    color: var(--white);
    padding: 0.75rem 1.5rem;
    border-radius: var(--radius);
    box-shadow: var(--shadow-md);
    z-index: 1000;
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      transform: translateY(1.25rem);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

  return (
    <div className="admin-dashboard">
      <style>{styles}</style>
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="admin-logo">
            <FontAwesomeIcon icon={faHotel} className="logo-icon" />
            <span className="logo-text">Pearl Tours & Travels</span>
          </div>
        </div>
        <ul className="sidebar-menu">
          <li
            className={activeTab === "dashboard" ? "active" : ""}
            onClick={() => setActiveTab("dashboard")}
          >
            <FontAwesomeIcon icon={faChartLine} />
            <span>Dashboard</span>
          </li>
          <li
            className={activeTab === "packages" ? "active" : ""}
            onClick={() => setActiveTab("packages")}
          >
            <FontAwesomeIcon icon={faMapMarkedAlt} />
            <span>Travel Packages</span>
          </li>
          <li
            className={activeTab === "blogs" ? "active" : ""}
            onClick={() => setActiveTab("blogs")}
          >
            <FontAwesomeIcon icon={faFileAlt} />
            <span>Blogs</span>
          </li>
          <li
            className={activeTab === "agents" ? "active" : ""}
            onClick={() => setActiveTab("agents")}
          >
            <FontAwesomeIcon icon={faUsers} />
            <span>Agents</span>
          </li>
          <li
            className={activeTab === "enquiries" ? "active" : ""}
            onClick={() => setActiveTab("enquiries")}
          >
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Enquiries</span>
          </li>
          <li
            className={activeTab === "visas" ? "active" : ""}
            onClick={() => setActiveTab("visas")}
          ><FontAwesomeIcon icon={faFileAlt} />
            {/* <FontAwesomeIcon icon={faDollarSign} /> */}
            <span>Visa Services</span>
          </li>
        </ul>
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="admin-content">
        {/* <div className="admin-header">
          <h1 className="page-title">
            {activeTab === "dashboard" && "Dashboard Overview"}
            {activeTab === "packages" && "Travel Packages Management"}
            {activeTab === "blogs" && "Blog Management"}
            {activeTab === "agents" && "Travel Agents Management"}
            {activeTab === "enquiries" && "Customer Enquiries"}
            {activeTab === "visas" && "Visa Services Management"}
          </h1>
          <div className="header-actions">
            <div className="search-bar">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="notification-btn"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <FontAwesomeIcon icon={faBell} />
              {notifications.filter((n) => !n.read).length > 0 && (
                <span className="notification-badge">
                  {notifications.filter((n) => !n.read).length}
                </span>
              )}
            </button>
            <button
              className="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">AD</div>
            </button>

            {showNotifications && (
              <div className="notifications-dropdown" ref={notificationsRef}>
                <div className="notifications-header">
                  <h3>Notifications</h3>
                  <button
                    className="action-btn"
                    onClick={() => setShowNotifications(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className="notifications-list">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${
                        !notification.read ? "unread" : ""
                      }`}
                      onClick={() => markNotificationAsRead(notification.id)}
                    >
                      <div className="notification-message">
                        {notification.message}
                      </div>
                      <div className="notification-time">
                        {notification.time}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            
          </div>
        </div> */}


        <div className="admin-header">
  <h1 className="page-title">
    {activeTab === "dashboard" && "Dashboard Overview"}
    {activeTab === "packages" && "Travel Packages Management"}
    {activeTab === "blogs" && "Blog Management"}
    {activeTab === "agents" && "Travel Agents Management"}
    {activeTab === "enquiries" && "Customer Enquiries"}
    {activeTab === "visas" && "Visa Services Management"}
  </h1>
  <div className="header-actions">
    <div className="search-bar">
      <FontAwesomeIcon icon={faSearch} className="search-icon" />
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
    <button
      className="user-menu-btn"
      onClick={() => setShowUserMenu(!showUserMenu)}
    >
      <div className="user-avatar">AD</div>
    </button>

    {showUserMenu && (
      <div className="user-dropdown" ref={userMenuRef}>
        <ul className="user-menu-list">
          <li className="user-menu-item" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </li>
        </ul>
      </div>
    )}
  </div>
</div>

        {activeTab === "dashboard" && (
          <div className="dashboard-overview">
            <div className="stats-container">
              <StatCard
                icon={faMapMarkedAlt}
                value={stats.packages}
                label="Total Packages"
                color="#3b82f6"
              />
              <StatCard
                icon={faCalendarAlt}
                value={stats.bookings}
                label="Total Bookings"
                color="#10b981"
              />
              <StatCard
                icon={faFileAlt}
                value={stats.blogs}
                label="Blog Posts"
                color="#f59e0b"
              />
             
              <StatCard
                icon={faUsers}
                value={stats.agents}
                label="Registered Agents"
                color="#ec4899"
              />
              <StatCard
                icon={faUserShield}
                value={stats.activeAgents}
                label="Active Agents"
                color="#14b8a6"
              />
              <StatCard
                icon={faDollarSign}
                value={stats.visas}
                label="Visa Services"
                color="#f97316"
              />
            </div>

            <div className="dashboard-sections">
              <div className="dashboard-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("packages")}
                >
                  <div className="section-title">
                    <FontAwesomeIcon icon={faMapMarkedAlt} />
                    <span>Recent Packages</span>
                  </div>
                  <FontAwesomeIcon
                    icon={
                      expandedSections.packages ? faChevronUp : faChevronDown
                    }
                    className="section-toggle"
                  />
                </div>
                {expandedSections.packages && (
                  <div className="section-content">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Package Name</th>
                          <th>Destination</th>
                          <th>Type</th>
                          {/* <th>Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPackages.slice(0, 5).map((pkg, index) => (
                          <tr key={index}>
                            <td>{pkg.name}</td>
                            <td>{pkg.destination}</td>
                            <td>
                              {pkg.type === "domestic"
                                ? "Domestic"
                                : "International"}
                            </td>
                            {/* <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() => handleEditPackage(pkg)}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() =>
                                    handleDeletePackage(pkg.id || pkg.name)
                                  }
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="dashboard-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("agents")}
                >
                  <div className="section-title">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>Recent Agents</span>
                  </div>
                  <FontAwesomeIcon
                    icon={expandedSections.agents ? faChevronUp : faChevronDown}
                    className="section-toggle"
                  />
                </div>
                {expandedSections.agents && (
                  <div className="section-content">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Mobile</th>
                          <th>Status</th>
                          {/* <th>Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAgents.slice(0, 5).map((agent, index) => (
                          <tr key={index}>
                            <td>
                              {agent.firstName} {agent.lastName}
                            </td>
                            <td>{agent.email}</td>
                            <td>{agent.mobile}</td>
                            <td>
                              <span
                                className={`status-badge ${
                                  agent.isActive
                                    ? "status-active"
                                    : "status-inactive"
                                }`}
                              >
                                {agent.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            {/* <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn"
                                  onClick={() =>
                                    toggleAgentStatus(agent.id, agent.isActive)
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      agent.isActive ? faToggleOn : faToggleOff
                                    }
                                  />
                                </button>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDeleteAgent(agent.id)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="dashboard-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("enquiries")}
                >
                  <div className="section-title">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Recent Enquiries</span>
                  </div>
                  <FontAwesomeIcon
                    icon={
                      expandedSections.enquiries ? faChevronUp : faChevronDown
                    }
                    className="section-toggle"
                  />
                </div>
                {expandedSections.enquiries && (
                  <div className="section-content">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Mobile</th>
                          <th>Destination</th>
                          {/* <th>Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEnquiries.slice(0, 5).map((enquiry, index) => (
                          <tr key={index}>
                            <td>{enquiry.name}</td>
                            <td>{enquiry.mobile}</td>
                            <td>{enquiry.destination}</td>
                            {/* <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() => handleEditEnquiry(enquiry)}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() =>
                                    handleDeleteEnquiry(enquiry)
                                  }
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "packages" && (
          <div className="dashboard-overview">
            <div className="form-container">
              <h2 className="form-title">
                <FontAwesomeIcon icon={isEditing ? faEdit : faPlus} />
                {isEditing ? "Edit Package" : "Add New Package"}
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Package Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={newPackage.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className="error-message">{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Destination</label>
                  <input
                    type="text"
                    className={`form-control ${errors.destination ? 'is-invalid' : ''}`}
                    name="destination"
                    value={newPackage.destination}
                    onChange={handleInputChange}
                  />
                  {errors.destination && <div className="error-message">{errors.destination}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <input
                    type="text"
                    className={`form-control ${errors.duration ? 'is-invalid' : ''}`}
                    name="duration"
                    value={newPackage.duration}
                    onChange={handleInputChange}
                    placeholder="e.g., 5 Days / 4 Nights"
                  />
                  {errors.duration && <div className="error-message">{errors.duration}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Package Type</label>
                  <div className={`radio-group ${errors.type ? 'error' : ''}`}>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="type"
                        value="domestic"
                        checked={newPackage.type === "domestic"}
                        onChange={handleInputChange}
                      />
                      Domestic
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="type"
                        value="international"
                        checked={newPackage.type === "international"}
                        onChange={handleInputChange}
                      />
                      International
                    </label>
                  </div>
                  {errors.type && <div className="error-message">{errors.type}</div>}
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Description</label>
                  <textarea
                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    name="description"
                    value={newPackage.description}
                    onChange={handleInputChange}
                  />
                  {errors.description && <div className="error-message">{errors.description}</div>}
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Prices</label>
                  <div className="chip-input-container">
                    <input
                      type="text"
                      className={`form-control chip-input ${errors.price ? 'is-invalid' : ''}`}
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      onKeyPress={handlePriceKeyPress}
                      placeholder="Add price option (e.g., ₹999 per person)"
                    />
                    <button
                      className="add-chip-btn"
                      onClick={handleAddPrice}
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                  {errors.price && <div className="error-message">{errors.price}</div>}
                  <div className="chip-container">
                    {priceValues.map((price, index) => (
                      <div key={index} className="chip">
                        {price}
                        <span
                          className="chip-remove"
                          onClick={() => handleRemovePrice(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">What's Included</label>
                  <div className="chip-input-container">
                    <input
                      type="text"
                      className="form-control chip-input"
                      value={includeInput}
                      onChange={(e) => setIncludeInput(e.target.value)}
                      placeholder="Add included item"
                    />
                    <button
                      className="add-chip-btn"
                      onClick={handleAddInclude}
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                  <div className="chip-container">
                    {newPackage.includes.map((item, index) => (
                      <div key={index} className="chip">
                        {item}
                        <span
                          className="chip-remove"
                          onClick={() => handleRemoveInclude(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">What's Excluded</label>
                  <div className="chip-input-container">
                    <input
                      type="text"
                      className="form-control chip-input"
                      value={excludeInput}
                      onChange={(e) => setExcludeInput(e.target.value)}
                      placeholder="Add excluded item"
                    />
                    <button
                      className="add-chip-btn"
                      onClick={handleAddExclude}
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                  <div className="chip-container">
                    {newPackage.excludes.map((item, index) => (
                      <div key={index} className="chip">
                        {item}
                        <span
                          className="chip-remove"
                          onClick={() => handleRemoveExclude(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Itinerary</label>
                  <div className="itinerary-container">
                    {Object.entries(newPackage.itinerary).map(
                      ([day, content]) => (
                        <div key={day} className="itinerary-day">
                          <div className="itinerary-header">
                            <span>{day}</span>
                            <button
                              className="remove-day-btn"
                              onClick={() => handleRemoveDay(day)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                          <div className="itinerary-content">
                            <textarea
                              className="form-control"
                              value={content}
                              onChange={(e) =>
                                handleItineraryChange(day, e.target.value)
                              }
                              placeholder={`Enter details for ${day}`}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  <button
                    className="add-day-btn"
                    onClick={handleAddDay}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Day
                  </button>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Package Images</label>
                  <div className="file-upload">
                    <label className="file-input-label">
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Select Images</span>
                      <input
                        type="file"
                        className="file-input"
                        onChange={handlePackageImageUpload}
                        multiple
                        accept="image/*"
                        ref={packageImagesRef}
                      />
                    </label>
                    <div className="file-upload-preview">
                      {existingImages.map((image, index) => (
                        <div key={`existing-${index}`} className="image-preview-container">
                          <img
                            src={image}
                            alt={`Package ${index}`}
                            className="image-preview"
                          />
                          <button 
                            type="button" 
                            className="remove-image-btn"
                            onClick={() => handleRemoveImage(index, true)}
                            title="Remove image"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                      {newImages.map((image, index) => (
                        <div key={`new-${index}`} className="image-preview-container">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New image ${index}`}
                            className="image-preview"
                          />
                          <button 
                            type="button" 
                            className="remove-image-btn"
                            onClick={() => handleRemoveImage(index, false)}
                            title="Remove image"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* <div className="form-group">
                  <label className="form-label">PDF Brochure (Optional)</label>
                  <div className="file-upload">
                    <label className="file-input-label">
                      <FontAwesomeIcon icon={faFilePdf} />
                      <span>Select PDF</span>
                      <input
                        type="file"
                        className="file-input"
                        onChange={(e) => setPdfFile(e.target.files[0])}
                        accept=".pdf"
                      />
                    </label>
                    {pdfFile && (
                      <div className="chip">
                        {pdfFile.name}
                        <span
                          className="chip-remove"
                          onClick={() => setPdfFile(null)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </div>
                    )}
                  </div>
                </div> */}
                <div className="form-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={resetForm}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddPackage}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={isEditing ? faSave : faPlus} />
                    {isEditing ? "Update Package" : "Add Package"}
                  </button>
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faMapMarkedAlt} />
                  <span>All Packages</span>
                </div>
              </div>
              <div className="section-content">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Package Name</th>
                      <th>Destination</th>
                      <th>Type</th>
                      <th>Duration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPackages.map((pkg, index) => (
                      <tr key={index}>
                        <td>{pkg.name}</td>
                        <td>{pkg.destination}</td>
                        <td>
                          {pkg.type === "domestic"
                            ? "Domestic"
                            : "International"}
                        </td>
                        <td>{pkg.duration}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEditPackage(pkg)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeletePackage(pkg)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "blogs" && (
          <div className="dashboard-overview">
            <div className="form-container">
              <h2 className="form-title">
                <FontAwesomeIcon icon={isEditingBlog ? faEdit : faPlus} />
                {isEditingBlog ? "Edit Blog Post" : "Add New Blog Post"}
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={newBlog.title}
                    onChange={handleBlogInputChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Author</label>
                  <input
                    type="text"
                    className="form-control"
                    name="author"
                    value={newBlog.author}
                    onChange={handleBlogInputChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-control"
                    name="category"
                    value={newBlog.category}
                    onChange={handleBlogInputChange}
                  >
                    <option value="general">General</option>
                    <option value="travel-tips">Travel Tips</option>
                    <option value="destination-guides">
                      Destination Guides
                    </option>
                    <option value="adventure">Adventure</option>
                    <option value="culture">Culture</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Featured Image</label>
                  <div className="file-upload">
                    <label className="file-input-label">
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Select Image</span>
                      <input
                        type="file"
                        className="file-input"
                        onChange={(e) =>
                          setNewBlog({
                            ...newBlog,
                            featuredImage: e.target.files[0],
                          })
                        }
                        accept="image/*"
                      />
                    </label>
                    {newBlog.featuredImage && (
                      <div className="file-upload-preview">
                        {newBlog.featuredImage instanceof File ? (
                          <img
                            src={URL.createObjectURL(newBlog.featuredImage)}
                            alt="Featured preview"
                            className="image-preview"
                          />
                        ) : (
                          <img
                            src={newBlog.featuredImage}
                            alt="Featured"
                            className="image-preview"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-control"
                    name="content"
                    value={newBlog.content}
                    onChange={handleBlogInputChange}
                    rows={10}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={resetBlogForm}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddBlog}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={isEditingBlog ? faSave : faPlus} />
                    {isEditingBlog ? "Update Blog" : "Publish Blog"}
                  </button>
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>All Blog Posts</span>
                </div>
              </div>
              <div className="section-content">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Category</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBlogs.map((blog, index) => (
                      <tr key={index}>
                        <td>{blog.title}</td>
                        <td>{blog.author}</td>
                        <td>
                          {blog.category
                            .split("-")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEditBlog(blog)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteBlog(blog)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "agents" && (
          <div className="dashboard-overview">
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faUsers} />
                  <span>All Travel Agents</span>
                </div>
              </div>
              <div className="section-content">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgents.map((agent, index) => (
                      <tr key={index}>
                        <td>
                          {agent.firstName} {agent.lastName}
                        </td>
                        <td>{agent.email}</td>
                        <td>{agent.mobile}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              agent.isActive
                                ? "status-active"
                                : "status-inactive"
                            }`}
                          >
                            {agent.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn"
                              onClick={() => toggleAgentStatus(agent)}
                            >
                              <FontAwesomeIcon
                                icon={agent.isActive ? faToggleOn : faToggleOff}
                              />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteAgent(agent)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "enquiries" && (
          <div className="dashboard-overview">
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>All Customer Enquiries</span>
                </div>
              </div>
              <div className="section-content">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>Destination</th>
                      <th>Travel Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEnquiries.map((enquiry, index) => (
                      <tr key={index}>
                        <td>{enquiry.name}</td>
                        <td>{enquiry.mobile}</td>
                        <td>{enquiry.email}</td>
                        <td>{enquiry.destination}</td>
                        <td>{enquiry.travelDate}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEditEnquiry(enquiry)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                             onClick={() => handleDeleteEnquiry(enquiry)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "visas" && (
          <div className="dashboard-overview">
            <div className="form-container">
              <h2 className="form-title">
                <FontAwesomeIcon icon={isEditingVisa ? faEdit : faPlus} />
                {isEditingVisa ? "Edit Visa Service" : "Add Visa Service"}
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Visiting Country</label>
                  <input
                    type="text"
                    className="form-control"
                    value={visaData.visitingCountry}
                    onChange={(e) =>
                      setVisaData({
                        ...visaData,
                        visitingCountry: e.target.value,
                      })
                    }
                    placeholder="Enter country name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Visa Fees</label>
                  <input
                    type="text"
                    className="form-control"
                    value={visaData.fees}
                    onChange={(e) =>
                      setVisaData({ ...visaData, fees: e.target.value })
                    }
                    placeholder="Enter visa fees"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Documents Required</label>
                  <div className="chip-input-container">
                    <input
                      type="text"
                      className="form-control chip-input"
                      value={currentInput.documentsRequired}
                      onChange={(e) =>
                        setCurrentInput({
                          ...currentInput,
                          documentsRequired: e.target.value,
                        })
                      }
                      placeholder="Add required document"
                    />
                    <button
                      className="add-chip-btn"
                      onClick={() => handleAddItem("documentsRequired")}
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                  <div className="chip-container">
                    {visaData.documentsRequired.map((doc, index) => (
                      <div key={index} className="chip">
                        {doc}
                        <span
                          className="chip-remove"
                          onClick={() =>
                            handleRemoveItem("documentsRequired", index)
                          }
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={resetVisaForm}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveVisa}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={isEditingVisa ? faSave : faPlus} />
                    {isEditingVisa ? "Update Visa" : "Add Visa"}
                  </button>
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faDollarSign} />
                  <span>All Visa Services</span>
                </div>
              </div>
              <div className="section-content">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Flag</th>
                      <th>Fees</th>
                      <th>Documents Required</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisas.map((visa, index) => (
                      <tr key={index}>
                        <td>{visa.visitingCountry}</td>
                        <td>
                          {visa.flag && (
                            <img
                              src={visa.flag}
                              alt={`${visa.visitingCountry} flag`}
                              style={{ width: "40px", height: "auto" }}
                            />
                          )}
                        </td>
                        <td>{visa.fees}</td>
                        <td>
                          <div className="chip-container">
                            {visa.documentsRequired &&
                              visa.documentsRequired.map((doc, i) => (
                                <div key={i} className="chip">
                                  {doc}
                                </div>
                              ))}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEditVisa(visa)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteVisa(visa)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
